/**
 * Table utility functions for data operations
 */

import type { TableDataRow, ColumnDefinition, SortDirection, PageSizeChoice } from '../types/table';

/**
 * Sort data by column and direction
 */
export const sortData = <T extends TableDataRow>(
  data: T[],
  column: keyof T | null,
  direction: SortDirection
): T[] => {
  if (!column || !direction) return data;

  return [...data].sort((a, b) => {
    const aVal = a[column];
    const bVal = b[column];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    let result = 0;

    // Handle different data types
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      result = aVal - bVal;
    } else if (typeof aVal === 'string' && typeof bVal === 'string') {
      result = aVal.localeCompare(bVal, 'no-NO');
    } else {
      result = String(aVal).localeCompare(String(bVal), 'no-NO');
    }

    return direction === 'asc' ? result : -result;
  });
};

/**
 * Simple wildcard matching function
 * % = matches any sequence of characters
 */
const matchesWildcard = (text: string, pattern: string): boolean => {
  // Convert wildcard pattern to regex
  const regexPattern = pattern
    .split('')
    .map(char => {
      if (char === '%') return '.*';
      // Escape other regex special characters
      return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('');
  
  try {
    const regex = new RegExp(`^${regexPattern}$`, 'i');
    return regex.test(text);
  } catch (e) {
    // If regex fails, fall back to simple string operations
    return text.toLowerCase().includes(pattern.replace(/%/g, '').toLowerCase());
  }
};

/**
 * Date filter helpers for client-side date range filtering
 */
interface DateFilter {
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
  op?: '<' | '>' | '<=' | '>=' | '=';
  single?: string; // YYYY-MM-DD for operator-based
}

const toDateOnly = (value: string): string | null => {
  if (!value) return null;
  // Handle ISO strings like "2026-04-16T00:00:00" and Norwegian "16.04.2026"
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  // Bruk LOKALE dato-komponenter — IKKE toISOString(), som konverterer til UTC og skyver
  // midnatt-tidsstempler (typisk DeliveryDate = 00:00) én dag tilbake i UTC+. Dette holder
  // filter-datoen identisk med det visningen (formatDate → toLocaleDateString) viser, slik at
  // f.eks. leveringsdato-filter ikke blir forskjøvet ett døgn.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`; // YYYY-MM-DD (lokal)
};

const parseDateFilter = (filterValue: string): DateFilter => {
  // Range: "2026-01-01..2026-04-16"
  const rangeSep = filterValue.indexOf('..');
  if (rangeSep > 0) {
    return {
      from: filterValue.slice(0, rangeSep).trim(),
      to: filterValue.slice(rangeSep + 2).trim(),
    };
  }
  // Prefix operators
  if (filterValue.startsWith('>=')) return { op: '>=', single: filterValue.slice(2).trim() };
  if (filterValue.startsWith('<=')) return { op: '<=', single: filterValue.slice(2).trim() };
  if (filterValue.startsWith('>')) return { op: '>', single: filterValue.slice(1).trim() };
  if (filterValue.startsWith('<')) return { op: '<', single: filterValue.slice(1).trim() };
  // Exact or partial match
  return { op: '=', single: filterValue };
};

const matchesDateFilter = (rowDate: string, filter: DateFilter): boolean => {
  if (filter.from || filter.to) {
    if (filter.from && rowDate < filter.from) return false;
    if (filter.to && rowDate > filter.to) return false;
    return true;
  }
  if (filter.op && filter.single) {
    switch (filter.op) {
      case '>=': return rowDate >= filter.single;
      case '<=': return rowDate <= filter.single;
      case '>': return rowDate > filter.single;
      case '<': return rowDate < filter.single;
      case '=': return rowDate.startsWith(filter.single);
    }
  }
  return true;
};

/**
 * Filter data based on text and dropdown filters
 */
export const filterData = <T extends TableDataRow>(
  data: T[],
  columns: ColumnDefinition<T>[],
  textFilters: Record<string, string>,
  dropdownFilters: Record<string, string[]>,
  globalSearch?: string
): T[] => {
  let filtered = data;

  // Apply global search first with wildcard support
  if (globalSearch && globalSearch.trim()) {
    const searchTerm = globalSearch.toLowerCase();
    filtered = filtered.filter(row =>
      columns.some(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return false;
        
        const valueText = value.toString().toLowerCase();
        
        // Handle wildcard patterns with %
        if (searchTerm.includes('%')) {
          return matchesWildcard(valueText, searchTerm);
        } else {
          // Normal contains search
          return valueText.includes(searchTerm);
        }
      })
    );
  }

  // Apply column text filters with wildcard and date range support
  Object.entries(textFilters).forEach(([columnKey, filterValue]) => {
    if (filterValue.trim()) {
      const col = columns.find(c => String(c.key) === columnKey);
      const isDateColumn = col?.type === 'date' || col?.filterType === 'date';

      if (isDateColumn) {
        // Date filter: supports range (from..to), prefix operators (>=, <=, >, <)
        const dateFilter = parseDateFilter(filterValue.trim());
        filtered = filtered.filter(row => {
          const value = row[columnKey as keyof T];
          if (value === null || value === undefined) return false;
          const rowDate = toDateOnly(value.toString());
          if (!rowDate) return false;
          return matchesDateFilter(rowDate, dateFilter);
        });
      } else {
        const searchTerm = filterValue.toLowerCase();
        filtered = filtered.filter(row => {
          const value = row[columnKey as keyof T];
          if (value === null || value === undefined) return false;

          const valueText = value.toString().toLowerCase();

          // Handle wildcard patterns with %
          if (searchTerm.includes('%')) {
            return matchesWildcard(valueText, searchTerm);
          } else {
            // Normal contains search
            return valueText.includes(searchTerm);
          }
        });
      }
    }
  });

  // Apply dropdown filters
  Object.entries(dropdownFilters).forEach(([columnKey, selectedValues]) => {
    if (selectedValues.length > 0) {
      filtered = filtered.filter(row => {
        const value = row[columnKey as keyof T]?.toString();
        return selectedValues.includes(value || '');
      });
    }
  });

  return filtered;
};

/**
 * Get paginated data
 */
export const paginateData = <T extends TableDataRow>(
  data: T[],
  currentPage: number,
  pageSize: number
): { paginatedData: T[]; totalPages: number } => {
  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);

  return { paginatedData, totalPages };
};

/**
 * Get distinct values for dropdown filters
 */
export const getDistinctValues = <T extends TableDataRow>(
  data: T[],
  columnKey: keyof T
): string[] => {
  const values = data
    .map(row => row[columnKey])
    .filter(val => val !== null && val !== undefined)
    .map(val => String(val));

  return Array.from(new Set(values)).sort();
};

/**
 * Calculate summary statistics for a column
 */
export const calculateSummary = <T extends TableDataRow>(
  data: T[],
  columnKey: keyof T,
  type: 'sum' | 'average' | 'count' | 'min' | 'max'
): number => {
  const values = data
    .map(row => row[columnKey])
    .filter(val => val !== null && val !== undefined && !isNaN(Number(val)))
    .map(val => Number(val));

  if (values.length === 0) return 0;

  switch (type) {
    case 'sum':
      return values.reduce((sum, val) => sum + val, 0);
    case 'average':
      return values.reduce((sum, val) => sum + val, 0) / values.length;
    case 'count':
      return values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return 0;
  }
};

/**
 * Export data to CSV format
 */
export const exportToCSV = <T extends TableDataRow>(
  data: T[],
  columns: ColumnDefinition<T>[],
  filename: string = 'warehouse-data.csv'
): void => {
  if (data.length === 0) return;

  // Semikolon som skilletegn + UTF-8 BOM: norsk/svensk Excel bruker semikolon som
  // listeskilletegn og trenger BOM for å gjenkjenne UTF-8 (æøå) — med komma uten BOM
  // havner alt i én kolonne når fila åpnes i Excel.
  const headers = columns.filter(col => col.visible).map(col => col.label);
  const csvContent = [
    headers.join(';'),
    ...data.map(row =>
      columns
        .filter(col => col.visible)
        .map(col => {
          let value: any = row[col.key];
          if (col.formatter) {
            value = col.formatter(value, row);
          }
          // ?? (ikke ||) så tallet 0 ikke eksporteres som tom celle
          const stringValue = String(value ?? '');
          if (stringValue.includes(';') || stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(';')
    )
  ].join('\r\n');

  // Download the CSV (BOM først så Excel gjenkjenner UTF-8)
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Save table configuration to localStorage
 */
export const saveTableConfig = <T extends TableDataRow>(
  persistenceKey: string,
  columns: ColumnDefinition<T>[],
  pageSize: PageSizeChoice
): void => {
  if (!persistenceKey) return;

  const config = {
    columns: columns.map(col => ({
      id: col.id,
      visible: col.visible,
      width: col.width,
      userWidth: col.userWidth === true,
      order: columns.indexOf(col)
    })),
    pageSize,
    timestamp: new Date().toISOString(),
    // v2 (TW 36396506): bredde lagres kun som brukervalg (userWidth). v1-bredder var
    // standardbredder og ignoreres ved innlasting, så tabellen får «best fit».
    version: 2
  };

  try {
    localStorage.setItem(`warehouse-table-${persistenceKey}`, JSON.stringify(config));
  } catch (error) {
    console.warn('Failed to save table configuration:', error);
  }
};

/**
 * Tolk lagret «Rader per side»-valg. Eldre lagrede oppsett kan inneholde et
 * konkret radantall fra den gamle «Alle»-implementasjonen — det beholdes som tall.
 */
export const parsePageSizeChoice = (
  stored: unknown,
  fallback: PageSizeChoice
): PageSizeChoice => {
  if (stored === 'all') return 'all';
  if (typeof stored === 'number' && Number.isFinite(stored) && stored > 0) return stored;
  return fallback;
};

/**
 * Load table configuration from localStorage
 */
export const loadTableConfig = (persistenceKey: string): any | null => {
  if (!persistenceKey) return null;

  try {
    const stored = localStorage.getItem(`warehouse-table-${persistenceKey}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load table configuration:', error);
    return null;
  }
};

/**
 * Create column factory for type-safe column definitions
 */
export const createColumnFactory = <T extends TableDataRow>() => {
  const baseColumn: Partial<ColumnDefinition<T>> = {
    visible: true,
    sortable: false,
    resizable: true,
    minWidth: 60,
    alignment: 'left',
    filterType: 'none'
  };

  return {
    createTextColumn: (
      id: string,
      key: keyof T,
      label: string,
      options?: Partial<ColumnDefinition<T>>
    ): ColumnDefinition<T> => ({
      ...baseColumn,
      id,
      key,
      label,
      width: 120,
      type: 'text',
      ...options
    } as ColumnDefinition<T>),

    createNumberColumn: (
      id: string,
      key: keyof T,
      label: string,
      options?: Partial<ColumnDefinition<T>>
    ): ColumnDefinition<T> => ({
      ...baseColumn,
      id,
      key,
      label,
      width: 100,
      type: 'number',
      alignment: 'right',
      ...options
    } as ColumnDefinition<T>),

    createCurrencyColumn: (
      id: string,
      key: keyof T,
      label: string,
      options?: Partial<ColumnDefinition<T>>
    ): ColumnDefinition<T> => ({
      ...baseColumn,
      id,
      key,
      label,
      width: 120,
      type: 'currency',
      alignment: 'right',
      ...options
    } as ColumnDefinition<T>),

    createDateColumn: (
      id: string,
      key: keyof T,
      label: string,
      options?: Partial<ColumnDefinition<T>>
    ): ColumnDefinition<T> => ({
      ...baseColumn,
      id,
      key,
      label,
      width: 100,
      type: 'date',
      ...options
    } as ColumnDefinition<T>),

    createBooleanColumn: (
      id: string,
      key: keyof T,
      label: string,
      options?: Partial<ColumnDefinition<T>>
    ): ColumnDefinition<T> => ({
      ...baseColumn,
      id,
      key,
      label,
      width: 80,
      type: 'boolean',
      alignment: 'center',
      ...options
    } as ColumnDefinition<T>),

    createStatusColumn: (
      id: string,
      key: keyof T,
      label: string,
      statusMap?: Record<string, { label: string; color: string }>,
      options?: Partial<ColumnDefinition<T>>
    ): ColumnDefinition<T> => ({
      ...baseColumn,
      id,
      key,
      label,
      width: 100,
      type: 'status',
      formatter: statusMap ? (value) => statusMap[String(value)]?.label || String(value) : undefined,
      ...options
    } as ColumnDefinition<T>),

    createCustomColumn: (definition: ColumnDefinition<T>): ColumnDefinition<T> => ({
      ...baseColumn,
      ...definition
    })
  };
};